import React, { FC, useRef, useState, useMemo } from 'react';
import type { MapRefs } from '@uiw/react-baidu-map';

import { BaiduMap, BaiduMapMarker, COORD_STANDARD } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

import { BaiduMapViewProps } from './BaiduMapView.types';
import { StyledBaiduMapView } from './BaiduMapView.styles';

/**
 * Decide the source coordinate datum of the incoming lat/lng.
 *
 * Baidu renders in BD-09. Mainland-China coordinates are GCJ-02; Hong Kong,
 * Macau, Taiwan and international coordinates are WGS-84. The country code alone
 * is unreliable (HK and Macau are returned as "CN"), but mainland-China
 * addresses always carry a stateProvince while HK/Macau do not — so mainland is
 * taken to mean countryCode === "CN" AND a stateProvince is present.
 */
export const getSourceCoordStandard = (
  countryCode?: string,
  hasStateProvince?: boolean
): COORD_STANDARD => {
  const isMainlandChina = countryCode?.toUpperCase() === 'CN' && !!hasStateProvince;
  return isMainlandChina ? COORD_STANDARD.GCJ02 : COORD_STANDARD.WGS84;
};

const DEFAULT_ZOOM = 10;

export const BaiduMapView: FC<BaiduMapViewProps> = props => {
  // Stable reference so it does not produce a new object on every render.
  const rawPosition = useMemo(
    () => ({ lat: props?.latitude || 0, lng: props?.longitude || 0 }),
    [props?.latitude, props?.longitude]
  );

  // Initial center/zoom bootstrap the map only. After first render they are
  // never changed by React, so re-renders can't reset the user's pan/zoom
  // (CNWEB-10104). Recentring happens imperatively once, after conversion.
  const [initialCenter] = useState(rawPosition);

  // Only the marker is driven by state — it does not move the viewport.
  const [markerPosition, setMarkerPosition] = useState(rawPosition);

  const convertedRef = useRef(false);

  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);
  const { BAIDU_MAP_API_KEY: apiKey } = clientEnvVars;

  const sourceCoordStandard = getSourceCoordStandard(props?.countryCode, props?.hasStateProvince);

  const handleMapRef = (mapRefs: MapRefs) => {
    const BMap = mapRefs?.BMap;
    const map = mapRefs?.map;
    if (!BMap || !map) {
      return;
    }
    // Ensure scroll-wheel zoom is enabled — some Baidu init paths leave it off.
    map.enableScrollWheelZoom?.();

    // Convert + recentre once. The guard stops this re-running on the map
    // re-inits that Baidu fires during zoom/drag.
    if (convertedRef.current) {
      return;
    }
    convertedRef.current = true;

    const convertor = new BMap.Convertor();
    convertor.translate(
      [new BMap.Point(rawPosition.lng, rawPosition.lat)],
      sourceCoordStandard,
      COORD_STANDARD.BD09,
      ({ points }) => {
        const converted = points?.[0];
        if (!converted) {
          // Conversion failed — unlock so it can retry rather than leaving the
          // marker at the raw (wrong) position.
          convertedRef.current = false;
          return;
        }
        // Marker via state; viewport via centerAndZoom so the map's internal
        // view state stays coherent and zoom keeps working. Centre and marker
        // share the same point, so the pin stays anchored to the hotel on zoom.
        setMarkerPosition({ lat: converted.lat, lng: converted.lng });
        map.centerAndZoom(new BMap.Point(converted.lng, converted.lat), map.getZoom() || DEFAULT_ZOOM);
      }
    );
  };

  return (
    <StyledBaiduMapView data-component-name="m-account-BaiduMapView">
      <div data-testid="account-BaiduMapView" className="g-map-container">
        <BaiduMap
          apiKey={apiKey}
          zoom={DEFAULT_ZOOM}
          center={initialCenter}
          getMapRef={handleMapRef}
          panoramaControl={false}
          mapControlProps={{ offset: { width: 10, height: 10 } }} // check its use
          children={
            <BaiduMapMarker id="baidu-marker" position={markerPosition} className="labels icon-l pb-1">
              <span className="icon-l icon-status-cobalt"></span>
            </BaiduMapMarker>
          }
          style={{
            height: '100%',
            width: '100% ',
            borderRadius: '1rem',
          }}
        />
      </div>
    </StyledBaiduMapView>
  );
};